import { useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ReactComponent as AscendingSortingIcon } from '../assets/icons/main/ascending_sorting.svg';
import { ReactComponent as DescendingSortingIcon } from '../assets/icons/main/descending_sorting.svg';
import { ReactComponent as NoSortIcon } from '../assets/icons/main/no-sort.svg';

import { ReactComponent as DeleteIcon } from '../assets/icons/main/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/main/plus-circle-icon.svg';
// earlier using -- plus-circle-icon-primary.svg';

import { useOutletContext } from 'react-router-dom';
import { get } from 'lodash';
import { useTheme } from '@mui/material';


const Styles = {
  tableWrapper: { display: 'flex', flexDirection: 'column', gap: 1 },
  dataGridWrapper: { width: '100%', marginTop: '10px' },
  dataGrid: {
    '&.MuiDataGrid-root': {
      borderColor: 'transparent !important',
    },
    '&.MuiDataGrid-root .MuiDataGrid-withBorderColor': {
      borderColor: 'transparent !important',
    },
    '& .MuiDataGrid-cell': {
      borderColor: 'transparent !important',
    },
    '& .MuiDataGrid-cell:hover': {
      borderColor: 'transparent !important',
    },
    '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
      outline: 'none !important',
    },
  },
  stack: {
    fontSize: '1rem',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: { width: '13px', height: '13px' },
  errorAddWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addNewBtn: { display: 'flex', alignSelf: 'flex-end', '& .MuiButton-startIcon': { mr: 0 } },
};

type RowDataType = { id: number; key: string; value: string };
interface PropsTypes {
  data: RowDataType[];
  setData: React.Dispatch<React.SetStateAction<RowDataType>>;
  error?: boolean;
}

interface CustomTextFieldPropsTypes {
  name: string;
  value: string;
  error: undefined | boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const CustomTextField = ({ name, value, error, onChange }: CustomTextFieldPropsTypes) => {
  const [fieldValue, setFieldValue] = useState(value);

  return (
    <TextField
      variant='outlined'
      name={name}
      size='small'
      value={fieldValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue(e.target.value);
        onChange(e);
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      error={error}
    />
  );
};

const MetaTagsTable = ({ data, setData, error }: PropsTypes) => {
  const themeData: any = useTheme();
  
  const [rows, setRows] = useState<{ id: number; key: string; value: string }[]>([]);

  useEffect(() => {
    if (data) {
      setRows(data);
    }
  }, []);

  const debounceOnChange = useMemo(
    () =>
      debounce((updatedRows) => {
        setData(updatedRows);
      }, 200),
    []
  );

  useEffect(() => {
    debounceOnChange(rows);
  }, [JSON.stringify(rows)]);

  const handleAddRow = () => {
    const id = rows.length + 1;
    setRows((oldRows) => [...oldRows, { id, key: '', value: '' }]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, rowId: string | number) => {
    const { name, value } = e.target;

    const updatedRows = rows.map((row: RowDataType) => {
      if (row.id === rowId) {
        return {
          ...row,
          [name]: value,
        };
      }
      return row;
    });

    setRows(updatedRows);
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows((prevRows) =>
      prevRows
        .filter((row) => row.id !== id)
        .map((row: RowDataType, idx: number) => {
          return { ...row, id: idx + 1 };
        })
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'key',
      headerName: 'Key',
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: (params) => {
        const fieldValue = (rows.find((row: RowDataType) => row.id === params.id) || {}).key || '';
        return (
          <CustomTextField
            name='key'
            value={fieldValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, params.id)}
            error={error && isEmpty(fieldValue)}
          />
        );
      },
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: 1,
      sortable: true,
      filterable: false,
      renderCell: (params) => {
        const fieldValue = (rows.find((row: RowDataType) => row.id === params.id) || {}).value || '';
        return (
          <CustomTextField
            name='value'
            value={fieldValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, params.id)}
            error={error && isEmpty(fieldValue)}
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: GridRowId }) => {
        return [<GridActionsCellItem icon={<DeleteIcon style={{color : themeData.palette.custom.light}} />} label='Delete' onClick={handleDeleteClick(id)} />];
      },
    },
  ];

  return (
    <Box sx={Styles.tableWrapper}>
      <Box sx={[Styles.dataGridWrapper, { height: rows.length ? '200px' : '100px' }]}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableColumnMenu
          hideFooterPagination
          hideFooter
          disableRowSelectionOnClick
          slots={{
            columnSortedAscendingIcon: () => <AscendingSortingIcon style={{...Styles.icon, color : themeData.palette.custom.light}} />,
            columnSortedDescendingIcon: () => <DescendingSortingIcon style={{...Styles.icon, color : themeData.palette.custom.light}} />,
            columnUnsortedIcon: () => <NoSortIcon style={{...Styles.icon, color : themeData.palette.custom.light}} />,
            noRowsOverlay: () => <Stack sx={{ ...Styles.stack, color: themeData.palette.text.primary}}>No metadata added!</Stack>,
          }}
          sx={Styles.dataGrid}
          rowHeight={64}
        />
      </Box>
      <Box sx={Styles.errorAddWrapper}>
        {error ? (
          <Typography variant='body2' color='error' fontSize='1rem'>
            Please enter Metadata!
          </Typography>
        ) : (
          <Box></Box>
        )}
        <Button variant='text' startIcon={<PlusIcon style={{color : themeData.palette.primary.main}}/>} onClick={handleAddRow} sx={Styles.addNewBtn}>
          Add New Item
        </Button>
      </Box>
    </Box>
  );
};

export default MetaTagsTable;
